import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { NEXT_TIER_NUDGE, getTier } from "@shared/gamification";
import Link from "@/components/Link";
import TierChip from "@/components/TierChip";
import PointsHistoryList from "@/components/PointsHistoryList";
import ReviewCard from "@/components/ReviewCard";
import { Progress } from "@/components/ui/progress";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { fetchPublicProfile, type PublicProfile as PublicProfileData } from "@/api/userService";
import type { Review } from "@/types/review";

// /u/@id — a contribution record, Arabic RTL, client-only and noindex.
//
// This component renders STRICTLY what the payload contains. The server already applied the §8
// visibility table, so there is no client-side visibility branching beyond "is the field
// present": a visitor's payload has no `progress` and a private profile's has no `reviews`, which
// is what makes the rule unbypassable rather than merely unrendered.
//
// The tab strip is the one place that reads `viewer` directly, and it is absent entirely for a
// visitor rather than disabled. A disabled tab advertises that private data exists.

const font = "var(--ys-font)";

const cardStyle: React.CSSProperties = {
  background: "var(--ys-paper)",
  border: "1px solid var(--ys-rule)",
  borderRadius: "var(--ys-r-md)",
  boxShadow: "var(--ys-shadow-2)",
  padding: 24,
  fontFamily: font,
};

const bodyStyle: React.CSSProperties = {
  fontFamily: font,
  fontSize: 14,
  color: "var(--ys-ink-2)",
  margin: 0,
};

const countStyle: React.CSSProperties = {
  fontFamily: font,
  fontSize: 20,
  fontWeight: 600,
  color: "var(--ys-ink)",
};

const Count: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <div style={{ textAlign: "center", minWidth: 72 }}>
    <div style={countStyle}>{value.toLocaleString("ar-EG")}</div>
    <div style={{ ...bodyStyle, fontSize: 12, color: "var(--ys-ink-3)" }}>{label}</div>
  </div>
);

const formatJoinDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString("ar-EG", { year: "numeric", month: "long" });
  } catch {
    return "";
  }
};

// The payload's review shape is not the store page's `Review`, so it is adapted rather than cast:
// ReviewCard renders a native, non-anonymous review here in its read-only form (no like handler,
// no comment handler, no owner actions), which is exactly what a contribution record needs.
const toReviewCardShape = (
  review: NonNullable<PublicProfileData["reviews"]>[number],
  author: PublicProfileData
): Review =>
  ({
    _id: review._id,
    user: {
      _id: author.id,
      full_name: author.full_name,
      avatar_url: author.avatar_url,
      tier: { level: author.tier.level, ar: author.tier.ar, en: author.tier.en },
    },
    text: review.text,
    overallRating: review.overallRating,
    ratingFactors: {},
    likesCount: review.likesCount,
    isLikedByCurrentUser: false,
    commentsCount: 0,
    comments: [],
    isAnonymous: false,
    isVerifiedPurchase: false,
    isExternal: false,
    images: review.images,
    createdAt: review.createdAt,
    updatedAt: review.createdAt,
  }) as unknown as Review;

const PublicProfile: React.FC<{ userId: string }> = ({ userId }) => {
  const { trackProfile } = usePostHogTracking();
  const [tab, setTab] = useState<"reviews" | "history">("reviews");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["publicProfile", userId],
    queryFn: () => fetchPublicProfile({ userId }),
    enabled: !!userId,
    staleTime: 60 * 1000,
    retry: false,
  });

  // `viewer_role` is the whole point of the event, which is also why nothing identifying the
  // profile's owner is a property: a name or an email belongs in identify() person properties.
  useEffect(() => {
    if (!data) return;
    trackProfile.viewed({
      viewer_role: data.viewer.isOwner ? "owner" : data.viewer.isAdmin ? "admin" : "visitor",
      tier_level: data.tier.level,
    });
  }, [data, trackProfile]);

  // Tier/points notifications deep-link to /u/<id>#rewards (Phase 46, D-09). This page is
  // client-rendered and the card only exists once the profile query resolves, so the browser's
  // native hash scroll found nothing on load. Scroll once, after the data has rendered.
  const rewardsScrolledRef = useRef(false);
  useEffect(() => {
    if (!data || rewardsScrolledRef.current || typeof window === "undefined") return;
    if (window.location.hash !== "#rewards") return;
    rewardsScrolledRef.current = true;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
    document
      .getElementById("rewards")
      ?.scrollIntoView({ block: "start", behavior: reduced ? "auto" : "smooth" });
  }, [data]);

  if (isLoading) {
    return (
      <div style={{ ...cardStyle, textAlign: "center" }}>
        <p style={bodyStyle}>جارِ التحميل…</p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div style={{ ...cardStyle, textAlign: "center" }}>
        <p style={bodyStyle}>الملف الشخصي غير موجود.</p>
      </div>
    );
  }

  const canSeeHistory = data.viewer.isOwner || data.viewer.isAdmin;

  const nudge = (() => {
    if (!data.showNudge || !data.progress?.pointsToNext || !data.progress.nextThreshold) {
      return null;
    }
    const nextAr = getTier({ points: data.progress.nextThreshold }).ar;
    // 40 is what a complete written review pays, so at or under it the concrete framing is true.
    return data.progress.pointsToNext <= 40
      ? NEXT_TIER_NUDGE.oneReviewAway({ tierAr: nextAr })
      : NEXT_TIER_NUDGE.pointsAway({ points: data.progress.pointsToNext, tierAr: nextAr });
  })();

  const progressPct = (() => {
    if (!data.progress?.nextThreshold) return null;
    const span = Math.max(1, data.progress.nextThreshold - data.tier.threshold);
    return Math.min(100, Math.max(0, ((data.points - data.tier.threshold) / span) * 100));
  })();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Identity card: tier, points and progress. The #rewards deep-link anchor. */}
      <div id="rewards" style={{ ...cardStyle, scrollMarginTop: 72 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          {data.avatar_url ? (
            <img
              src={data.avatar_url}
              alt=""
              style={{ width: 56, height: 56, borderRadius: "var(--ys-r-pill)", objectFit: "cover" }}
            />
          ) : (
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "var(--ys-r-pill)",
                background: "var(--ys-bg-2)",
                color: "var(--ys-ink-2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: font,
                fontWeight: 600,
                fontSize: 20,
              }}
            >
              {data.full_name?.[0]?.toUpperCase() || "م"}
            </div>
          )}

          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontFamily: font, fontSize: 18, fontWeight: 600, color: "var(--ys-ink)" }}>
                {data.full_name}
              </span>
              <TierChip tier={data.tier} size="md" />
              {/* Axis B. The ONLY badge here allowed to read as a trust signal. */}
              {data.isVerified && (
                <span
                  style={{
                    fontFamily: font,
                    fontSize: 11,
                    fontWeight: 500,
                    color: "var(--ys-success)",
                    background: "var(--ys-success-soft)",
                    border: "1px solid var(--ys-success)",
                    borderRadius: "var(--ys-r-pill)",
                    padding: "1px 7px",
                  }}
                >
                  موثّق
                </span>
              )}
            </div>
            <p style={{ ...bodyStyle, fontSize: 12, color: "var(--ys-ink-3)", marginTop: 4 }}>
              عضو منذ {formatJoinDate(data.joinDate)}
            </p>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 20,
            marginTop: 20,
            paddingTop: 16,
            borderTop: "1px solid var(--ys-rule)",
            flexWrap: "wrap",
          }}
        >
          <Count value={data.counts.reviews} label="تقييم" />
          <Count value={data.counts.photos} label="صورة" />
          <Count value={data.counts.helpfulVotes} label="صوت مفيد" />
          <Count value={data.points} label="نقطة" />
        </div>

        {/* Present only for the owner and admins: the server omits `progress` for a visitor. */}
        {progressPct !== null && (
          <div style={{ marginTop: 16 }}>
            <Progress value={progressPct} />
            {nudge && <p style={{ ...bodyStyle, marginTop: 8 }}>{nudge}</p>}
          </div>
        )}
      </div>

      {data.visibility === "private" ? (
        <div style={cardStyle}>
          <p style={bodyStyle}>هذا الملف الشخصي خاص، والتقييمات مش ظاهرة.</p>
        </div>
      ) : (
        <>
          {canSeeHistory && (
            <div style={{ display: "flex", gap: 8 }}>
              {(
                [
                  { key: "reviews", label: "التقييمات" },
                  { key: "history", label: "سجل النقاط" },
                ] as const
              ).map((entry) => (
                <button
                  key={entry.key}
                  type="button"
                  onClick={() => {
                    setTab(entry.key);
                    if (entry.key === "history") {
                      trackProfile.historyTabOpened({
                        viewer_role: data.viewer.isOwner ? "owner" : "admin",
                        tier_level: data.tier.level,
                      });
                    }
                  }}
                  style={{
                    background: tab === entry.key ? "var(--ys-primary)" : "transparent",
                    color: tab === entry.key ? "#fff" : "var(--ys-ink-2)",
                    border: `1px solid ${tab === entry.key ? "var(--ys-primary)" : "var(--ys-rule)"}`,
                    borderRadius: "var(--ys-r-pill)",
                    padding: "8px 16px",
                    minHeight: 36,
                    fontFamily: font,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {entry.label}
                </button>
              ))}
            </div>
          )}

          {tab === "history" && canSeeHistory ? (
            <div style={cardStyle}>
              <PointsHistoryList userId={userId} locale="ar" />
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {(data.reviews ?? []).length === 0 && (
                <div style={cardStyle}>
                  <p style={bodyStyle}>لسه مافيش تقييمات منشورة.</p>
                </div>
              )}
              {(data.reviews ?? []).map((review) => (
                <div key={review._id}>
                  {review.store && (
                    <Link
                      to={`/store/${review.store.slug || review.store._id}`}
                      style={{
                        fontFamily: font,
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--ys-ink-2)",
                        textDecoration: "none",
                        display: "inline-block",
                        marginBottom: 4,
                      }}
                    >
                      {review.store.name}
                    </Link>
                  )}
                  <ReviewCard
                    review={toReviewCardShape(review, data)}
                    storeName={review.store?.name}
                    storeLogoUrl={review.store?.logoUrl}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PublicProfile;
